import axios, { type AxiosError } from 'axios';
import { type ApiError, isApiError } from './ApiError';

/**
 * Returns true when the error represents a network connectivity issue or a
 * request timeout — conditions under which the offline enrollment path should
 * execute instead of surfacing an error to the user.
 */
export function isNetworkOrTimeoutError(err: unknown): boolean {
  const classified = classifyApiError(err);
  return classified.kind === 'network' || classified.kind === 'timeout';
}

export function classifyApiError(error: unknown): ApiError {
  // httpClient interceptor already classified it — pass through to avoid double-wrapping
  if (isApiError(error)) return error;

  if (axios.isAxiosError(error)) {
    return classifyAxiosError(error);
  }
  if (isPersonUserBug(error)) {
    return {
      kind: 'backend_bug',
      bugId: 'personUser',
      message: 'Backend warning: Undefined variable personUser. El timelog probablemente se grabó.',
      original: error,
    };
  }
  if (error instanceof Error) {
    return { kind: 'unknown', message: error.message, original: error };
  }
  return { kind: 'unknown', message: 'Unknown error', original: error };
}

function classifyAxiosError(error: AxiosError): ApiError {
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return { kind: 'timeout', message: 'La conexión tardó demasiado' };
    }
    return { kind: 'network', message: 'No hay conexión con el servidor' };
  }

  const { status, data } = error.response;
  const body = data as Record<string, unknown> | undefined;
  // msg field (login errors use 'msg', not 'message')
  const msgField = body?.['msg'] as string | undefined;
  const statusField = body?.['status'] as string | number | undefined;
  const code = (body?.['code'] ?? body?.['error_code'] ?? msgField) as string | undefined;
  const rawMessage = body?.['message'] ?? body?.['error_message'];
  // Backend sometimes sends `message` as an array/object (Laravel-style validation
  // errors, e.g. [{ id_device: "if121" }]) instead of a string — coerce so callers
  // never get "[object Object]" when they log or display this.
  const messageFromApi = typeof rawMessage === 'string' ? rawMessage : undefined;

  // Bug documentado: SQLSTATE[22P02]
  if (status === 500 && typeof messageFromApi === 'string' && messageFromApi.includes('22P02')) {
    return {
      kind: 'backend_bug',
      bugId: 'sqlstate_22p02',
      message: 'id_device enviado como string en lugar de integer.',
      original: error,
    };
  }

  // Bug documentado: fopen
  if (status === 500 && typeof messageFromApi === 'string' && messageFromApi.includes('fopen')) {
    return {
      kind: 'backend_bug',
      bugId: 'fopen_photo',
      message: 'La foto debe tener prefijo "data:image/jpeg;base64,".',
      original: error,
    };
  }

  if (status === 401) {
    // Token expirado: { status: 'et401', message: 'TOKEN HAS EXPIRED.' }
    if (statusField === 'et401' || code === 'et401') {
      return { kind: 'authentication', code: 'token_expired', message: messageFromApi ?? 'Sesión expirada' };
    }
    // Credenciales inválidas: { error: 'Unauthorized', msg: 'invalid_credentials', status: 401 }
    if (msgField === 'invalid_credentials' || code === 'invalid_credentials') {
      return { kind: 'authentication', code: 'invalid_credentials', message: 'Correo o contraseña incorrectos' };
    }
    return { kind: 'authentication', code: 'no_token', message: messageFromApi ?? 'Sin autenticación' };
  }

  if (status === 403) {
    if (code === 'device_not_authorized') {
      return { kind: 'business', code, message: messageFromApi ?? 'Dispositivo no autorizado' };
    }
    return { kind: 'authorization', message: messageFromApi ?? 'Sin permisos' };
  }

  if (status === 404) {
    if (code === 'face_not_recognized' || code === 'card_not_found') {
      return { kind: 'business', code, message: messageFromApi ?? 'No reconocido' };
    }
    return { kind: 'not_found', message: messageFromApi ?? 'Recurso no encontrado' };
  }

  if (status === 400) {
    // Serialize body once for shape-agnostic marker detection
    const flatBody = safeStringify(data);
    
    // LICENSE_CLOCK_EXCEEDED — backend rejects device enrollment when the
    // license limit is reached. Shape: { status: 400, message: { "498": "LICENSE_CLOCK_EXCEEDED" } }
    // We scan the serialized body to catch this nested object structure.
    // MUST check for the exact marker to avoid false positives.
    if (flatBody.includes('LICENSE_CLOCK_EXCEEDED')) {
      return {
        kind: 'business',
        code: 'license_clock_exceeded',
        message: 'Licencia Clock excedida',
      };
    }

    // LICENSE_CLOCK_NOT_FOUND — backend rejects when the organization has no
    // Clock license at all. Shape: { status: 400, message: { 499: "LICENSE_CLOCK_NOT_FOUND" } }
    if (flatBody.includes('LICENSE_CLOCK_NOT_FOUND')) {
      return {
        kind: 'business',
        code: 'license_clock_not_found',
        message: 'Licencia Clock no encontrada',
      };
    }

    // DUPLICATE_TIMELOG_ID — backend rejects a timelog whose (person + timestamp)
    // already exists (trigger_ma_timelogs_duplicate_before). Happens when the
    // user clicks the marking button twice in quick succession. This is NOT a
    // transient error — retrying with the same payload will always fail — so
    // we classify it as a distinct business error so the UI can show a
    // dedicated message instead of the generic "petición inválida".
    //
    // The backend nests the DUPLICATE marker deep and inconsistently:
    //   { status: "110", message: { error: "…DUPLICATE_TIMELOG_ID:253111…", code: "P0001" } }
    //   { status: 400,  message: "…DUPLICATE_TIMELOG_ID:…" }              // shape observed elsewhere
    //   raw string body with the marker
    // Serializing the whole body and scanning the resulting text is the only
    // shape-agnostic way to catch every variant. MUST run BEFORE the
    // `status === '110'` validation branch (line ~127) or duplicates get
    // misclassified as validation errors and swallowed as virtualSuccess.
    if (flatBody.includes('DUPLICATE_TIMELOG_ID')) {
      return {
        kind: 'business',
        code: 'duplicate_timelog',
        message: typeof messageFromApi === 'string' ? messageFromApi : 'Marcaje duplicado',
      };
    }
    // Validación status '110' con campo errors
    if (body?.['status'] === '110') {
      const fields = extractValidationFields(body);
      const fieldsMessage = Object.entries(fields)
        .map(([field, codes]) => `${field}: ${codes.join(', ')}`)
        .join('; ');
      return { kind: 'validation', fields, message: messageFromApi ?? (fieldsMessage || 'Validación fallida') };
    }
    // Error 746 (sin rostro en enroll/photo) puede llegar como 400
    if (body?.['status'] === 746) {
      return { kind: 'business', code: '746', message: messageFromApi ?? 'No se detectó rostro' };
    }
    const fields = extractValidationFields(body);
    return { kind: 'validation', fields, message: messageFromApi ?? 'Petición inválida' };
  }

  if (status === 422) {
    return { kind: 'validation', fields: extractValidationFields(body), message: messageFromApi ?? 'Validación fallida' };
  }

  if (status === 429) {
    const retryAfter = error.response.headers['retry-after'] as string | undefined;
    const retryAfterMs = retryAfter ? Number(retryAfter) * 1000 : 30_000;
    return { kind: 'rate_limit', retryAfterMs, message: messageFromApi ?? 'Demasiadas peticiones' };
  }

  if (status >= 500) {
    return { kind: 'server', status, message: messageFromApi ?? 'Error interno del servidor' };
  }

  if (code) {
    return { kind: 'business', code, message: messageFromApi ?? `Error ${code}` };
  }

  return { kind: 'unknown', message: messageFromApi ?? `HTTP ${status}`, original: error };
}

/**
 * Serialize any response body into a searchable string.
 *
 * Some backends nest error markers deep inside objects (e.g. Laravel-style
 * `{ status: "110", message: { error: "…DUPLICATE_TIMELOG_ID…", code: "P0001" } }`).
 * A single `body.message.includes(...)` check misses those. Scanning the
 * whole serialized shape catches every variant.
 *
 * Falls back to `String(value)` when JSON.stringify chokes (circular refs).
 */
function safeStringify(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function isPersonUserBug(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' && message.includes('Undefined variable') && message.includes('personUser');
}

function extractValidationFields(body: Record<string, unknown> | undefined): Record<string, string[]> {
  if (!body) return {};
  if (body['errors'] && typeof body['errors'] === 'object') {
    return body['errors'] as Record<string, string[]>;
  }
  // Laravel style: messages is an array of {field: code} objects
  if (Array.isArray(body['message'])) {
    const result: Record<string, string[]> = {};
    for (const item of body['message'] as Record<string, string>[]) {
      for (const [field, code] of Object.entries(item)) {
        if (!result[field]) result[field] = [];
        result[field]!.push(code);
      }
    }
    return result;
  }
  return {};
}
